import React, { useEffect, useState, useRef } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Oval } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Container from "../Layouts/Container";
import { Image, message } from "antd";
import { allCustomerList, sendSMS } from "../../API/SmsRequest";
import { sessionExpire } from "../../Scripts/Helper";
import { logoutAdmin } from "../../Action/authAction";
import Select from "react-select";
import { useFormik } from "formik";
import * as yup from "yup";
import { productTitleListAPIBySearch } from "../../API/manageProducts";

const SendSmsForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.authReducer.authData);
  const authToken = useSelector((state) => state.authReducer.token);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  // Customer-related states
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [searchCustomer, setSearchCustomer] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Product-related states
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null); // Single product ID
  const [searchProduct, setSearchProduct] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");

  // AbortController refs for cancelling API calls

  const productAbortControllerRef = useRef(null);

  // Custom styles for react-select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      padding: "0",
      minHeight: "38px",
      border: "1px solid #ced4da",
      borderRadius: "4px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 8px",
    }),
    indicatorsContainer: () => ({
      display: "none",
    }),
    multiValue: (provided) => ({
      ...provided,
      margin: "2px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0",
      padding: "0",
    }),
  };

  const handleSelectChange = (event) => {
    const value = Number(event.target.value);
    sendSMSForm.setFieldValue("customer_type", value);
    setShowDropdown(value === 2);
  };

  // Fetch customers
  const fetchCustomers = async () => {
    const config = {
      headers: {
        Authorization: `Bearer ${authToken}`,
        time_zone: userTimeZone,
      },
    };

    try {
      const response = await allCustomerList(config, searchCustomer);
      if (
        response.data.status &&
        response.data.data.rows &&
        response.data.data.rows.length > 0
      ) {
        const customerData = response.data.data.rows.map((customer) => ({
          id: customer.id,
          text: `${customer.full_name} - ${customer.phone}`,
        }));

        setCustomers((prevCustomers) => {
          const existingCustomersMap = new Map(
            prevCustomers.map((customer) => [customer.id, customer])
          );

          customerData.forEach((customer) => {
            existingCustomersMap.set(customer.id, customer);
          });

          return Array.from(existingCustomersMap.values());
        });
      } else {
        setCustomers([]);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      if (err.code === "ERR_NETWORK") {
        toast.error("Something went wrong! Please try again later.");
      } else {
        let res = sessionExpire(err);
        toast.error(res.message);
        if (res.status) {
          dispatch(logoutAdmin());
          navigate("/login");
        }
      }
    }
  };

  // Fetch products
  const fetchProductData = async (searchQuery = "") => {
    // Cancel previous product API call if it exists
    if (productAbortControllerRef.current) {
      productAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    productAbortControllerRef.current = new AbortController();
    setSpinnerLoading(true);
    const config = {
      headers: { Authorization: `Bearer ${authToken}` },
      signal: productAbortControllerRef.current.signal, // Add abort signal
    };

    try {
      const response = await productTitleListAPIBySearch(config, searchQuery);
      if (
        response.data.status &&
        response.data.data.products &&
        response.data.data.products.length > 0
      ) {
        const productData = response.data.data.products.map((product) => ({
          id: product.id,
          text: product.title,
        }));

        setProducts(productData); // Replace products, no merging needed
      } else {
        setProducts([]);
      }
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
        console.log("Product API request was cancelled");
        setSpinnerLoading(false);
        return; // Early return to avoid further processing
      }
      console.error("Error fetching products:", err);
      if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
        toast.error("Something went wrong! Please try again later");
      } else {
        let res = sessionExpire(err);
        toast.error(res.message);
        if (res.status) {
          dispatch(logoutAdmin());
          navigate("/login");
        }
      }
    }
    setSpinnerLoading(false);
  };

  
  // Debounce customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchCustomer);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchCustomer]);

  // Debounce product search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProductSearch(searchProduct);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchProduct]);

  // Fetch customers when debounced search changes
  useEffect(() => {
    fetchCustomers();
  }, [debouncedSearch]);

  // Fetch products when debounced product search changes
  useEffect(() => {
    fetchProductData(debouncedProductSearch);
  }, [debouncedProductSearch]);

  // Cleanup function to cancel any ongoing requests when component unmounts
  useEffect(() => {
    return () => {
      if (productAbortControllerRef.current) {
        productAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Formik setup
  const sendSMSForm = useFormik({
    initialValues: {
      customer_type: "",
      coupon_code: "",
      message: "",
      image: null,
      selectedCustomers: [],
      product_id: "", // Single product ID instead of array
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      customer_type: yup.string().required("Please select type of customer"),
      coupon_code: yup.string().required("Coupon code is required"),
      message: yup.string().required("Message is required"),
      image: yup.mixed().nullable(),
      product_id: yup.string().optional(), // Validate single product ID
    }),
    onSubmit: async (values, { resetForm }) => {
      setBtnLoading(true);

      const formData = new FormData();
      formData.append("customer_type", values.customer_type);
      formData.append("coupon_code", values.coupon_code);
      formData.append("message", values.message);
      formData.append("product_id", values.product_id); // Send single product_id

      if (values.image) {
        formData.append("image", values.image);
      }

      // Add selected customers to FormData
      if (values.customer_type === 2) {
        values.selectedCustomers.forEach((customerId, index) => {
          formData.append(`selectedCustomers[${index}]`, customerId);
        });
      }

      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };

      await sendSMS(config, formData)
        .then((res) => {
          toast.success("Form submitted successfully");
          setSelectedCustomers([]);
          setSelectedProduct(null);
          resetForm();
        })
        .catch((err) => {
          if (err.code === "ERR_NETWORK") {
            toast.error("Something went wrong! Please try again later");
          } else {
            let res = sessionExpire(err);
            toast.error(res.message);
            if (res.status) {
              dispatch(logoutAdmin());
              navigate("/login");
            }
          }
        });

      setBtnLoading(false);
    },
  });

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      {user && (
        <Container>
          <div className="nk-content mt-5">
            <div className="container-fluid">
              <div className="nk-content-inner">
                <div className="nk-content-body">
                  <div className="row g-gs mt-3">
                    <div className="col-md-12">
                      <div className="row g-gs">
                        <form
                          className="row"
                          onSubmit={sendSMSForm.handleSubmit}
                        >
                          <div className="col-md-6">
                            {/* Customer Type */}
                            <div className="form-group">
                              <div className="form-control-wrap whatNewInput">
                                <label className="form-label">
                                  Select Customer Type
                                </label>
                                <select
                                  className="form-control form-control-lg"
                                  placeholder="Select Type Of Customer"
                                  value={sendSMSForm.values.customer_type}
                                  onChange={handleSelectChange}
                                  name="customer_type"
                                >
                                  <option value="0">Select One</option>
                                  <option value="1">All Customers</option>
                                  <option value="2">Selected Customer</option>
                                </select>
                                {sendSMSForm.values.customer_type < 1 &&
                                  sendSMSForm.touched.customer_type &&
                                  sendSMSForm.errors.customer_type && (
                                    <div
                                      className="invalid-feedback"
                                      style={{ display: "block" }}
                                    >
                                      {sendSMSForm.errors.customer_type}
                                    </div>
                                  )}
                                {showDropdown && (
                                  <>
                                    <label className="form-label">
                                      Select Customers
                                    </label>
                                    <Select
                                      isMulti
                                      styles={customStyles}
                                      className="custom-select-container"
                                      options={
                                        customers?.map((customer) => ({
                                          value: customer.id,
                                          label: customer.text,
                                        })) || []
                                      }
                                      value={selectedCustomers
                                        ?.map((id) => {
                                          const customer = customers?.find(
                                            (c) => c.id === id
                                          );
                                          return customer
                                            ? {
                                                value: id,
                                                label: customer.text,
                                              }
                                            : null;
                                        })
                                        .filter(Boolean)}
                                      onInputChange={(inputValue) => {
                                        setSearchCustomer(inputValue);
                                        return inputValue;
                                      }}
                                      onChange={(selectedOptions) => {
                                        const selectedValues = selectedOptions
                                          ? selectedOptions.map(
                                              (option) => option.value
                                            )
                                          : [];
                                        sendSMSForm.setFieldValue(
                                          "selectedCustomers",
                                          selectedValues
                                        );
                                        setSelectedCustomers(selectedValues);
                                      }}
                                      placeholder="Search by customer's name or mobile number"
                                      noOptionsMessage={() =>
                                        "No such customer found"
                                      }
                                    />
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Upload Image */}
                            <div className="form-group">
                              <div className="form-control-wrap whatNewInput">
                                <label className="form-label">
                                  Upload Image
                                </label>
                                <input
                                  type="file"
                                  className="form-control form-control-lg"
                                  accept="image/*"
                                  onChange={(e) => {
                                    if (e.target.files.length > 0) {
                                      sendSMSForm.setFieldValue(
                                        "image",
                                        e.target.files[0]
                                      );
                                    }
                                  }}
                                />
                                {sendSMSForm.touched.image &&
                                  sendSMSForm.errors.image && (
                                    <div
                                      className="invalid-feedback"
                                      style={{ display: "block" }}
                                    >
                                      {sendSMSForm.errors.image}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* Product Selection */}
                            <div className="form-group">
                              <div className="form-control-wrap whatNewInput">
                                <label className="form-label">
                                  Select Product
                                </label>
                                <Select
                                  styles={customStyles}
                                  className="custom-select-container"
                                  options={
                                    products?.map((product) => ({
                                      value: product.id,
                                      label: product.text,
                                    })) || []
                                  }
                                  value={
                                    selectedProduct
                                      ? {
                                          value: selectedProduct,
                                          label:
                                            products?.find(
                                              (p) => p.id === selectedProduct
                                            )?.text || "",
                                        }
                                      : null
                                  }
                                  onMenuOpen={() => {
                                    setSelectedProduct(null);
                                    sendSMSForm.setFieldValue("product_id", "");
                                  }}
                                  // onInputChange={(inputValue) => {
                                  //   setSearchProduct(inputValue);
                                  //   return inputValue;
                                  // }}
                                  onInputChange={(inputValue, { action }) => {
                                    if (action === "input-change") {
                                      setSearchProduct(inputValue);
                                    }
                                    return inputValue;
                                  }}
                                  onChange={(selectedOption) => {
                                    const selectedValue = selectedOption
                                      ? selectedOption.value
                                      : "";
                                    sendSMSForm.setFieldValue(
                                      "product_id",
                                      selectedValue
                                    );
                                    setSelectedProduct(selectedValue);
                                  }}
                                  placeholder="Search by product title"
                                  noOptionsMessage={() =>
                                    "No such product found"
                                  }
                                />
                                {sendSMSForm.touched.product_id &&
                                  sendSMSForm.errors.product_id && (
                                    <div
                                      className="invalid-feedback"
                                      style={{ display: "block" }}
                                    >
                                      {sendSMSForm.errors.product_id}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* Coupon Code */}
                            <div className="form-group">
                              <div className="form-control-wrap whatNewInput">
                                <label className="form-label">
                                  Coupon Code
                                </label>
                                <input
                                  type="text"
                                  className="form-control form-control-lg"
                                  placeholder="Coupon Code"
                                  name="coupon_code"
                                  {...sendSMSForm.getFieldProps("coupon_code")}
                                />
                                {sendSMSForm.touched.coupon_code &&
                                  sendSMSForm.errors.coupon_code && (
                                    <div
                                      className="invalid-feedback"
                                      style={{ display: "block" }}
                                    >
                                      {sendSMSForm.errors.coupon_code}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* Message */}
                            <div className="form-group">
                              <div className="form-control-wrap whatNewInput">
                                <label className="form-label">Message</label>
                                <textarea
                                  type="text"
                                  className="form-control form-control-lg"
                                  placeholder="Write a message..."
                                  name="message"
                                  {...sendSMSForm.getFieldProps("message")}
                                />
                                {sendSMSForm.touched.message &&
                                  sendSMSForm.errors.message && (
                                    <div
                                      className="invalid-feedback"
                                      style={{ display: "block" }}
                                    >
                                      {sendSMSForm.errors.message}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* Submit Button */}
                            {btnLoading ? (
                              <div className="flex justify-center items-center">
                                <Oval
                                  height="30"
                                  width="30"
                                  color="var(--ps-main)"
                                  secondaryColor="var(--ps-main)"
                                  ariaLabel="oval-loading"
                                  strokeWidth={4}
                                  strokeWidthSecondary={4}
                                  visible={true}
                                />
                              </div>
                            ) : (
                              <button
                                type="submit"
                                className="btn btn-primary d-inline w-100"
                              >
                                Submit
                              </button>
                            )}
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      )}
    </>
  );
};

export default SendSmsForm;
